import type { Transition, Variants } from 'framer-motion'

/**
 * Every duration and curve in the member app comes from here.
 *
 * Motion that disagrees with itself is what makes an interface feel cheap: a
 * card that eases differently from the sheet it opened, a list that staggers
 * faster than the page it sits on. One file, one set of numbers.
 *
 * Nothing here checks `prefers-reduced-motion` — `<MotionConfig
 * reducedMotion="user">` in main.tsx does that once, for everything.
 */

export const DURATION = {
  fast: 0.16,
  base: 0.22,
  slow: 0.32,
} as const

/** Matches --ease-out-soft in globals.css. */
const EASE_OUT_SOFT = [0.22, 1, 0.36, 1] as const

/** Settles without the overshoot wobble a springier config gives buttons. */
const springSoft: Transition = {
  type: 'spring',
  stiffness: 420,
  damping: 38,
  mass: 0.9,
}

/** For anything that changes size or position and must not look elastic. */
export const springLayout: Transition = {
  type: 'spring',
  stiffness: 520,
  damping: 44,
  mass: 0.8,
}

/** The house entrance: come up a little, fade in. */
export const fadeRise: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DURATION.base, ease: EASE_OUT_SOFT },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: DURATION.fast, ease: EASE_OUT_SOFT },
  },
}

export const fade: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.base } },
  exit: { opacity: 0, transition: { duration: DURATION.fast } },
}

/**
 * Parent of a staggered list.
 *
 * The cap matters. Staggering 200 climbing routes at 40ms each means the last
 * one arrives eight seconds after the first, so anything past `STAGGER_CAP`
 * children should render without the variant — see `staggerIndex` below.
 */
export const staggerParent: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.04, delayChildren: 0.02 },
  },
}

const STAGGER_CAP = 12

/**
 * Per-child delay for lists that cannot use `staggerChildren` — an infinite
 * scroll appending page two must not re-animate page one, so children are
 * animated by index instead of by parent orchestration.
 */
export function staggerIndex(index: number) {
  return {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: DURATION.base,
      ease: EASE_OUT_SOFT,
      delay: index < STAGGER_CAP ? index * 0.04 : 0,
    },
  }
}

/** Physical feedback on anything tappable. Subtle on purpose: 1.5%. */
export const pressable = {
  whileTap: { scale: 0.985 },
  transition: springSoft,
} as const

/** Bottom sheet: slides from the bottom edge, backdrop crossfades. */
export const sheetPanel: Variants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: springLayout },
  exit: { y: '100%', transition: { duration: DURATION.base, ease: EASE_OUT_SOFT } },
}

export const sheetBackdrop: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.base } },
  exit: { opacity: 0, transition: { duration: DURATION.fast } },
}
